/** @jsxImportSource @emotion/react */
import React from 'react'
import ReactSelect from 'react-select'
import { useDebounce } from 'use-debounce'
import { useGetLabUsersQuery } from 'store/services/users'

export type OwnerOption = { value: number; label: string }

type LabUser = { id: number | string; firstName?: string | null; lastName?: string | null; email?: string | null }

const toOption = (user: LabUser): OwnerOption => ({
  value: Number(user.id),
  label: [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || `User #${user.id}`,
})

type Props = {
  value: OwnerOption | null
  onChange: (owner: OwnerOption | null) => void
  isClearable?: boolean
  placeholder?: string
  invalid?: boolean
  disabled?: boolean
  // Offered ahead of the searched users, e.g. "Me" in a filter.
  leadingOptions?: OwnerOption[]
}

// Active lab users, searched by name as the biller types.
export const OwnerSelect: React.FC<Props> = ({
  value,
  onChange,
  isClearable = false,
  placeholder = 'Owner…',
  invalid = false,
  disabled = false,
  leadingOptions = [],
}) => {
  const [search, setSearch] = React.useState('')
  const [debouncedSearch] = useDebounce(search, 300)
  const { data, isFetching } = useGetLabUsersQuery({
    search: debouncedSearch || undefined,
    active: true,
    page: 0,
    size: 50,
  } as never)

  // The users endpoint pages its results; the query is typed loosely upstream.
  const users = (data as unknown as { items?: LabUser[] } | undefined)?.items
  const options: OwnerOption[] = [...leadingOptions, ...(Array.isArray(users) ? users.map(toOption) : [])]

  return (
    <ReactSelect<OwnerOption>
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 2000 }),
        control: (base) => ({ ...base, minHeight: 38, ...(invalid ? { borderColor: '#dc3545' } : {}) }),
      }}
      classNamePrefix="select"
      isClearable={isClearable}
      isSearchable
      isDisabled={disabled}
      isLoading={isFetching}
      placeholder={placeholder}
      value={value}
      options={options}
      filterOption={() => true}
      onInputChange={(input, meta) => {
        if (meta.action === 'input-change') {
          setSearch(input)
        }
      }}
      onChange={(option) => onChange(option ?? null)}
    />
  )
}
